import { type Graph, nodeById } from '../models/graph';

export interface ResidualEdge {
  readonly from: string;
  readonly to: string;
  capacity: number;
  readonly originalEdgeId: string | null;
  reverse: ResidualEdge;
}

export type ResidualGraph = Map<string, ResidualEdge[]>;

export function buildResidualGraph(graph: Graph): ResidualGraph {
  const residual: ResidualGraph = new Map();
  for (const node of graph.nodes) residual.set(node.id, []);

  const link = (
    from: string,
    to: string,
    capacity: number,
    originalEdgeId: string | null,
  ): ResidualEdge => {
    const edge = { from, to, capacity, originalEdgeId } as ResidualEdge;
    return edge;
  };

  for (const edge of graph.edges) {
    const forward = link(edge.sourceId, edge.targetId, edge.capacity, edge.id);
    const backward = link(edge.targetId, edge.sourceId, 0, null);
    forward.reverse = backward;
    backward.reverse = forward;
    residual.get(edge.sourceId)?.push(forward);
    residual.get(edge.targetId)?.push(backward);

    if (!graph.directed) {
      const forward2 = link(edge.targetId, edge.sourceId, edge.capacity, edge.id);
      const backward2 = link(edge.sourceId, edge.targetId, 0, null);
      forward2.reverse = backward2;
      backward2.reverse = forward2;
      residual.get(edge.targetId)?.push(forward2);
      residual.get(edge.sourceId)?.push(backward2);
    }
  }

  return residual;
}

export function bfs(
  residual: ResidualGraph,
  sourceId: string,
  sinkId: string,
): ResidualEdge[] | null {
  const visited = new Set<string>([sourceId]);
  const parentEdge = new Map<string, ResidualEdge>();
  const queue: string[] = [sourceId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const edge of residual.get(current) ?? []) {
      if (!visited.has(edge.to) && edge.capacity > 0) {
        visited.add(edge.to);
        parentEdge.set(edge.to, edge);

        if (edge.to === sinkId) {
          const path: ResidualEdge[] = [];
          let node = sinkId;
          while (node !== sourceId) {
            const e = parentEdge.get(node)!;
            path.push(e);
            node = e.from;
          }
          return path.reverse();
        }
        queue.push(edge.to);
      }
    }
  }

  return null;
}

function runEdmondsKarp(residual: ResidualGraph, sourceId: string, sinkId: string): number {
  let totalFlow = 0;

  for (;;) {
    const path = bfs(residual, sourceId, sinkId);
    if (!path) break;

    let bottleneck = Infinity;
    for (const edge of path) bottleneck = Math.min(bottleneck, edge.capacity);
    for (const edge of path) {
      edge.capacity -= bottleneck;
      edge.reverse.capacity += bottleneck;
    }
    totalFlow += bottleneck;
  }

  return totalFlow;
}

export function edmondsKarp(graph: Graph, sourceId: string, sinkId: string): number {
  if (sourceId === sinkId) return 0;
  if (!nodeById(graph, sourceId) || !nodeById(graph, sinkId)) return 0;
  return runEdmondsKarp(buildResidualGraph(graph), sourceId, sinkId);
}

export function edmondsKarpResidual(graph: Graph, sourceId: string, sinkId: string): ResidualGraph {
  const residual = buildResidualGraph(graph);
  if (sourceId !== sinkId && nodeById(graph, sourceId) && nodeById(graph, sinkId)) {
    runEdmondsKarp(residual, sourceId, sinkId);
  }
  return residual;
}
